import { useState, useEffect } from 'react';
import { IconCopy, IconCheck, IconSettings } from '@tabler/icons-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import MainCard from '../components/MainCard';
import PageHeader from '../components/PageHeader';

export function CsvToJsonPage() {
  const [input, setInput] = useState('');
  const [output, setOutput] = useState('');
  const [parseNumbers, setParseNumbers] = useState(true);
  const [outputFormat, setOutputFormat] = useState<'array' | 'object'>('array');
  const [minify, setMinify] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!input.trim()) {
      setOutput('');
      setError(null);
      return;
    }

    try {
      const lines = input.split('\n').filter(line => line.trim().length > 0);
      if (lines.length === 0) {
        setOutput('');
        setError(null);
        return;
      }

      // Detect separator (comma, semicolon, or tab)
      const firstLine = lines[0];
      let separator = ',';
      if (firstLine.includes('\t')) {
        separator = '\t';
      } else if (firstLine.includes(';')) {
        separator = ';';
      } else if (firstLine.includes(',')) {
        separator = ',';
      }

      // Parse CSV
      const parseCsvLine = (line: string): string[] => {
        const result: string[] = [];
        let current = '';
        let inQuotes = false;
        
        for (let i = 0; i < line.length; i++) {
          const char = line[i];
          
          if (char === '"') {
            if (inQuotes && line[i + 1] === '"') {
              current += '"';
              i++;
            } else {
              inQuotes = !inQuotes;
            }
          } else if (char === separator && !inQuotes) {
            result.push(current.trim());
            current = '';
          } else {
            current += char;
          }
        }
        result.push(current.trim());
        
        return result;
      };

      const parsedLines = lines.map(line => parseCsvLine(line));
      const numColumns = Math.max(...parsedLines.map(line => line.length));

      // If single column, output as array
      if (numColumns === 1) {
        const values = parsedLines.map(line => {
          const value = line[0] || '';
          return parseNumbers && !isNaN(Number(value)) && value.trim() !== '' 
            ? Number(value) 
            : value;
        });
        const json = minify 
          ? JSON.stringify(values)
          : JSON.stringify(values, null, 2);
        setOutput(json);
        setError(null);
        return;
      }

      // Multi-column: check if first line is header
      const hasHeader = parsedLines.length > 1;
      let headers: string[] = [];
      let dataLines = parsedLines;

      if (hasHeader && numColumns > 1) {
        headers = parsedLines[0].map((h, idx) => h || `column${idx + 1}`);
        dataLines = parsedLines.slice(1);
      } else {
        headers = Array.from({ length: numColumns }, (_, i) => `column${i + 1}`);
        dataLines = parsedLines;
      }

      // Create objects
      const objects = dataLines.map(line => {
        const obj: Record<string, any> = {};
        headers.forEach((header, idx) => {
          const value = line[idx] || '';
          obj[header] = parseNumbers && !isNaN(Number(value)) && value.trim() !== '' 
            ? Number(value) 
            : value;
        });
        return obj;
      });

      let json: string;
      if (outputFormat === 'object' && objects.length > 0) {
        const keyField = headers[0];
        const hash: Record<string, any> = {};
        objects.forEach(obj => {
          const key = String(obj[keyField]);
          if (Object.keys(obj).length > 1) {
            const { [keyField]: _, ...rest } = obj;
            hash[key] = rest;
          } else {
            hash[key] = obj[keyField];
          }
        });
        json = minify 
          ? JSON.stringify(hash)
          : JSON.stringify(hash, null, 2);
      } else {
        json = minify 
          ? JSON.stringify(objects)
          : JSON.stringify(objects, null, 2);
      }

      setOutput(json);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to parse CSV');
      setOutput('');
    }
  }, [input, parseNumbers, outputFormat, minify]);

  const handleCopy = async () => {
    if (!output) return;
    try {
      await navigator.clipboard.writeText(output);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Copy failed
    }
  };

  return (
    <div>
      <PageHeader
        title="CSV / Column to JSON"
        subtitle="Convert CSV or column data to JSON format"
      />

      <MainCard>
        <div className="flex flex-col gap-6">
          {/* Settings */}
          <div className="flex items-center gap-4 flex-wrap rounded-lg bg-muted/50 p-4">
            <IconSettings className="size-5 text-muted-foreground" />
            <div className="flex items-center gap-2">
              <Checkbox
                id="parseNumbers"
                checked={parseNumbers}
                onCheckedChange={(checked) => setParseNumbers(checked as boolean)}
              />
              <Label htmlFor="parseNumbers">Parse numbers</Label>
            </div>
            <div className="flex items-center gap-2">
              <Label>Output Format</Label>
              <Select value={outputFormat} onValueChange={(v) => setOutputFormat(v as 'array' | 'object')}>
                <SelectTrigger className="w-[160px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="array">Array</SelectItem>
                  <SelectItem value="object">Object (Hash)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="minify"
                checked={minify}
                onCheckedChange={(checked) => setMinify(checked as boolean)}
              />
              <Label htmlFor="minify">Minify JSON</Label>
            </div>
          </div>

          {/* Input */}
          <div className="space-y-2">
            <p className="text-sm font-medium">CSV or Column Data</p>
            <Textarea
              rows={10}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={"name,age,city\nJohn,25,New York\nJane,30,Boston\n\nOr simple column:\nValue 1\nValue 2\nValue 3"}
              className="font-mono text-sm"
            />
            <p className="text-xs text-muted-foreground">
              Enter CSV data (comma, semicolon, or tab separated) or simple column values (one per line)
            </p>
          </div>

          {/* Error */}
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Output */}
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <p className="text-sm font-medium">JSON Output</p>
              <Button
                onClick={handleCopy}
                disabled={!output}
                variant="outline"
                size="sm"
              >
                {copied ? <IconCheck className="size-4" /> : <IconCopy className="size-4" />}
                {copied ? 'Copied!' : 'Copy'}
              </Button>
            </div>
            <Textarea
              rows={14}
              value={output}
              readOnly
              placeholder="JSON output will appear here..."
              className="font-mono text-sm bg-muted/50"
            />
          </div>
        </div>
      </MainCard>
    </div>
  );
}
